import { Box, Typography } from '@mui/material';
import { Button } from '@components/button';

/** Generic LIO support inbox — the fallback when the brokerage has no assigned sales user. */
const LIO_SUPPORT_EMAIL = 'service@lioinsurance.com';

type BrokerageBindBlockedCalloutProps = {
  brokerageName: string;
  /** The brokerage's onboarding submission id — target of the "View Brokerage Status" deep link. */
  onboardingSubmissionId?: string;
  /** The brokerage's assigned sales user email; falls back to the LIO support inbox when absent. */
  salesContactEmail?: string | null;
  /** Only internal users can open the onboarding submission workbench, so the link is internal-only. */
  isInternal: boolean;
};

/**
 * In-card "Binding action required" callout on the Review & Finalize page, shown when the quote's
 * brokerage is approved for quoting only (lifecycleStatus "Onboarding") and cannot yet bind. The quote
 * stays Ready to Bind — the user can still save, edit, and generate documents — but binding is blocked
 * (also enforced server-side) until the brokerage is fully appointed. Internal users get a
 * "View Brokerage Status" deep link into the brokerage's onboarding submission workbench.
 */
export const BrokerageBindBlockedCallout = ({
  brokerageName,
  onboardingSubmissionId,
  salesContactEmail,
  isInternal,
}: BrokerageBindBlockedCalloutProps) => {
  const contactEmail = salesContactEmail || LIO_SUPPORT_EMAIL;
  const showStatusLink = isInternal && Boolean(onboardingSubmissionId);

  return (
    <Box sx={sxStyles.container}>
      <Box sx={sxStyles.iconBadge} aria-hidden>
        !
      </Box>
      <Box sx={sxStyles.body}>
        <Typography sx={sxStyles.eyebrow}>Binding action required</Typography>
        <Typography sx={sxStyles.headline}>Your Brokerage is not currently approved to bind this quote</Typography>
        <Typography sx={sxStyles.copy}>
          The quote remains Ready to Bind. You may save, edit, or generate documents, but the Brokerage appointment must
          be completed before a policy can be issued.
        </Typography>

        <Box sx={sxStyles.detailBox}>
          <Box>
            <Typography sx={sxStyles.detailLabel}>Brokerage</Typography>
            <Typography sx={sxStyles.detailValue}>{brokerageName}</Typography>
          </Box>
        </Box>

        <Box sx={sxStyles.actions}>
          {showStatusLink ? (
            <Button
              variant='contained'
              sx={sxStyles.statusButton}
              // Open in a new tab so the in-progress bind review — and any unsaved edits — is preserved.
              onClick={() =>
                window.open(
                  `/portal-admin/brokerage-onboarding/${onboardingSubmissionId}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              View Brokerage Status
            </Button>
          ) : null}
          <Typography sx={sxStyles.help}>
            Need help? Contact{' '}
            <Box component='a' href={`mailto:${contactEmail}`} sx={sxStyles.helpLink}>
              {contactEmail}
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const sxStyles = {
  container: {
    display: 'flex',
    gap: 1.5,
    p: 2.5,
    my: 2,
    borderRadius: '8px',
    border: '1px solid #EAD9B0',
    borderLeft: '4px solid #C57A00',
    backgroundColor: '#FEF9EE',
  },
  iconBadge: {
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: '999px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#E5A000',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1,
    mt: '2px',
  },
  body: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  eyebrow: {
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: 11,
    fontWeight: 700,
    color: '#8A6A17',
  },
  headline: { fontSize: 18, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 },
  copy: { fontSize: 14, color: 'text.secondary' },
  detailBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 48px',
    mt: 0.5,
    p: 1.5,
    borderRadius: '6px',
    backgroundColor: '#F5EDDA',
  },
  detailLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontSize: 10,
    fontWeight: 700,
    color: '#8A8578',
  },
  detailValue: { fontSize: 14, fontWeight: 700, color: 'text.primary' },
  actions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 1 },
  statusButton: {
    backgroundColor: '#0F7B76',
    '&:hover': { backgroundColor: '#0C6763' },
  },
  help: { fontSize: 13, color: 'text.secondary' },
  helpLink: { color: '#0F7B76', fontWeight: 600, textDecoration: 'none' },
} as const;
